import React, { useContext, useEffect, useState } from 'react';
import {
    getTenantConfigFromPathname,
    getTenantsConfiguration,
    isPathAffectedByTenantConfig,
    TenantConfig
} from '~/shared/utils/tenant';
import { useRouter } from 'next/router';
import { useIntl } from 'react-intl';

export type TenantContextType = {
    tenantConfig: TenantConfig | null;
    tenantsConfigs: Array<TenantConfig> | null;
};

const defaultTenantContext = { tenantConfig: null, tenantsConfigs: null };

const TenantContext = React.createContext<TenantContextType>(defaultTenantContext);
const useTenantContext = () => useContext(TenantContext);

type TenantContextProviderProps = {
    children: React.ReactNode;
    tenantConfig: TenantConfig | null;
    tenantsConfigs: Array<TenantConfig>;
};

const TenantContextProvider = (props: TenantContextProviderProps) => {
    const router = useRouter();
    const {
        asPath,
        pathname,
        query: { tenant, id }
    } = router;
    const [tenantsConfigs, setTenantsConfigs] = useState<Array<TenantConfig> | null>(
        props.tenantsConfigs?.length > 0 ? props.tenantsConfigs : null
    );
    const [tenantConfig, setTenantConfig] = useState<{
        config: TenantConfig;
        asPath: string;
    } | null>(
        props.tenantConfig
            ? {
                  config: props.tenantConfig,
                  asPath: asPath
              }
            : null
    );
    const { formatMessage } = useIntl();

    useEffect(() => {
        const fetchTenantsConfigs = async () => {
            const tenantsConfig = await getTenantsConfiguration();
            setTenantsConfigs(tenantsConfig);
        };
        if (!tenantsConfigs || tenantsConfigs.length === 0) {
            fetchTenantsConfigs();
        }
    }, []);

    useEffect(() => {
        const fetchTenantConfig = async (tenantsConfigs: Array<TenantConfig>) => {
            const newTenantConfig = await getTenantConfigFromPathname({
                tenantsConfig: tenantsConfigs,
                asPath: asPath,
                pathname: pathname,
                query: { tenant, id },
                formatMessage: formatMessage
            });
            if (newTenantConfig) {
                setTenantConfig({
                    config: newTenantConfig,
                    asPath: asPath
                });
            }
        };

        if (tenantConfig && !isPathAffectedByTenantConfig(asPath, pathname)) {
            setTenantConfig(null);
        }

        if (
            tenantsConfigs &&
            isPathAffectedByTenantConfig(asPath, pathname) &&
            tenantConfig?.asPath !== asPath
        ) {
            fetchTenantConfig(tenantsConfigs);
        }
    }, [asPath, tenant, id, tenantsConfigs, pathname]);

    return (
        <TenantContext.Provider
            value={{ tenantConfig: tenantConfig?.config ?? null, tenantsConfigs }}
        >
            {props.children}
        </TenantContext.Provider>
    );
};
export { TenantContextProvider, useTenantContext };
